<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import Button from '../../Button.svelte';
  import Input from '../../Input.svelte';
  import { charitiesSearchService } from '../../../lib/api/admin/charities-search.service';
  import { api } from '../../../lib/api/client';
  import toast from 'svelte-french-toast';

  export let showModal = false;
  
  const dispatch = createEventDispatcher();
  
  let charityName = '';
  let contactPersonName = '';
  let contactEmail = '';
  let message = '';
  let checkingDuplicate = false;
  let duplicateWarning = '';
  let submitting = false;
  
  let checkTimeout: ReturnType<typeof setTimeout>;
  
  async function checkDuplicateName() {
    if (!charityName.trim() || charityName.trim().length < 2) {
      duplicateWarning = '';
      return;
    }

    if (checkTimeout) {
      clearTimeout(checkTimeout);
    }

    checkTimeout = setTimeout(async () => {
      checkingDuplicate = true;
      duplicateWarning = '';
      
      try {
        const results = await charitiesSearchService.searchCharities(charityName.trim(), 5);
        const exactMatch = results.find(c => c.name.toLowerCase() === charityName.trim().toLowerCase());
        
        if (exactMatch) {
          duplicateWarning = `Warning: A charity with the name "${exactMatch.name}" already exists. Please verify this is not a duplicate before proceeding.`;
        } else if (results.length > 0) {
          const similarNames = results.map(c => c.name).join(', ');
          duplicateWarning = `Similar charity names found: ${similarNames}. Please verify this is not a duplicate.`;
        }
      } catch (error) {
        console.error('Error checking duplicate:', error);
        // Don't show error to user, just continue
      } finally {
        checkingDuplicate = false;
      }
    }, 500);
  }
  
  function closeModal() {
    showModal = false;
    resetForm();
  }
  
  async function submitInvite() {
    if (!charityName.trim() || !contactEmail.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    submitting = true;
    
    try {
      const response = await api.post<{ success: boolean; message?: string }>('/user-invitation/invite-charity', {
        name: charityName.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: '',
        website: '',
        reason_for_request: message.trim() || undefined,
        head_office_location: ''
      });

      if (response.data.success) {
        toast.success('Charity invitation sent successfully');
        dispatch('charityInvited');
        closeModal();
      } else {
        toast.error(response.data.message || 'Failed to send invitation');
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to send invitation';
      toast.error(errorMsg);
    } finally {
      submitting = false;
    }
  }
  
  function resetForm() {
    charityName = '';
    contactPersonName = '';
    contactEmail = '';
    message = '';
    duplicateWarning = '';
    checkingDuplicate = false;
    if (checkTimeout) {
      clearTimeout(checkTimeout);
    }
  }
</script>

{#if showModal}
  <div class="modal-backdrop">
    <div class="modal-container">
      <div class="modal-content">
        <h2>Invite a New Charity</h2>
        <p class="modal-description">
          Complete the form below to invite a charity to join Leave2Give.<br>
          They'll receive an email with instructions to complete registration.
        </p>

        <div class="info-note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <span>Your name and email will be included in the invitation so the charity knows who referred them.</span>
        </div>
        
        <div class="form-group">
          <Input
            type="text"
            placeholder="Enter charity name"
            bind:value={charityName}
            on:input={checkDuplicateName}
            fullWidth={true}
            label="Charity Name"
            required={true}
            disabled={submitting}
          />
          {#if checkingDuplicate}
            <p class="checking-message">Checking for duplicates...</p>
          {/if}
          {#if duplicateWarning}
            <div class="duplicate-warning" role="alert">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              {duplicateWarning}
            </div>
          {/if}
        </div>
        
        <div class="form-group">
          <Input
            type="text"
            placeholder="Enter contact person's name"
            bind:value={contactPersonName}
            fullWidth={true}
            label="Contact Person Name"
          />
        </div>
        
        <div class="form-group">
          <Input
            type="email"
            placeholder="Enter contact email"
            bind:value={contactEmail}
            fullWidth={true}
            label="Contact Email"
          />
        </div>
        
        <div class="form-group">
          <label for="optional-message">Optional Message</label>
          <textarea
            id="optional-message"
            placeholder="Enter a message to include in the invitation email"
            bind:value={message}
            rows="3"
          ></textarea>
        </div>
        
        <div class="modal-actions">
          <Button variant="primary" on:click={submitInvite} fullWidth={true} disabled={submitting || checkingDuplicate}>
            {submitting ? 'Sending...' : 'Submit Invite'}
          </Button>
          <Button variant="secondary" on:click={closeModal} fullWidth={true} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-container {
    width: 100%;
    max-width: 500px;
    margin: 0 20px;
  }
  
  .modal-content {
    background-color: white;
    border-radius: 8px;
    padding: 32px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  h2 {
    font-size: 24px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 8px 0;
    text-align: center;
  }
  
  .modal-description {
    text-align: center;
    color: #6b7280;
    font-size: 14px;
    margin-bottom: 16px;
    line-height: 1.4;
  }

  .info-note {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem;
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    font-size: 0.8125rem;
    color: #1e40af;
    line-height: 1.4;
    text-align: left;
  }

  .info-note svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: #3b82f6;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }
  
  textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    min-height: 80px;
  }
  
  textarea:focus {
    outline: none;
    border-color: #f0b429;
    box-shadow: 0 0 0 2px rgba(240, 180, 41, 0.2);
  }
  
  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
  }
  
  /* Override Button component styling */
  .modal-actions :global(button.primary) {
    background-color: #f0b429;
    color: black;
  }
  
  .modal-actions :global(button.secondary) {
    background-color: #e5e7eb;
    color: #374151;
  }
  .modal-actions :global(button) {
    width: 100%;
    height: 52px;
    border-radius: 100px;
    padding: 10px;
    color: black;
    font-weight: 500;
    background-color: #f0b429;
  }

  .checking-message {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.5rem;
    font-style: italic;
  }

  .duplicate-warning {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.5rem;
    padding: 0.75rem;
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 6px;
    color: #92400e;
    font-size: 0.875rem;
    line-height: 1.4;
  }

  .duplicate-warning svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
</style> 